import * as React from 'react'
import { PieChart, Pie, Label, LegendProps } from "recharts"
import { motion } from "framer-motion"
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartConfig } from "@/components/ui/chart"

export interface DonutChartData {
  name: string
  value: number
  fill: string
}

interface DonutChartProps {
  data: DonutChartData[]
  chartConfig: ChartConfig
  isLoading?: boolean
  showLegend?: boolean
  showCenterLabel?: boolean
  centerLabelValue?: string | number
  centerLabelSubtext?: string
  innerRadius?: number
  outerRadius?: number
  strokeWidth?: number
  className?: string
  height?: number
  legendHeight?: number
  formatValue?: (value: number) => string
}

const DonutChart = ({
  data,
  chartConfig,
  isLoading = false,
  showLegend = true,
  showCenterLabel = true,
  centerLabelValue,
  centerLabelSubtext = "Total",
  innerRadius = 60,
  outerRadius = 100,
  strokeWidth = 5,
  className = "",
  height = 300,
  legendHeight = 72,
  formatValue = (value) => value.toString()
}: DonutChartProps) => {
  // Calculate total value for center label
  const totalValue = React.useMemo(() => {
    return data.reduce((acc, curr) => acc + curr.value, 0)
  }, [data])

  // Calculate responsive legend text size based on number of items
  const legendTextSize = React.useMemo(() => {
    const itemCount = data.length
    if (itemCount <= 2) return 'text-sm'
    if (itemCount <= 4) return 'text-xs'
    return 'text-[10px]' // Very small for 5+ items
  }, [data.length])

  const legendGap = React.useMemo(() => {
    const itemCount = data.length
    if (itemCount <= 2) return 'gap-4'
    if (itemCount <= 4) return 'gap-2'
    return 'gap-1' // Tighter spacing for 5+ items
  }, [data.length])

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ height }}>
        <div className="animate-pulse rounded-full bg-muted" 
             style={{ width: outerRadius * 2, height: outerRadius * 2 }} />
      </div>
    )
  }

  if (!data.length) {
    return (
      <div className={`flex items-center justify-center flex-col ${className}`} style={{ height }}>
        <div className="text-muted-foreground text-center">
          <p className="text-lg font-medium">No data available</p>
          <p className="text-sm">Chart will appear when data is available</p>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
      className={`w-full ${className}`}
      style={{ height, willChange: "opacity, transform" }}
    >
      <ChartContainer config={chartConfig} className="w-full h-full">
        <PieChart>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent hideLabel />}
          />
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            strokeWidth={strokeWidth}
            stroke="hsl(var(--background))"
            minAngle={8}
          >
            {showCenterLabel && (
              <Label
                content={({ viewBox }) => {
                  if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                    const displayValue = centerLabelValue !== undefined 
                      ? centerLabelValue 
                      : formatValue(totalValue)
                    
                    return (
                      <text
                        x={viewBox.cx}
                        y={viewBox.cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                      >
                        <tspan
                          x={viewBox.cx}
                          y={viewBox.cy}
                          className="fill-foreground text-2xl font-bold"
                        >
                          {displayValue}
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 20}
                          className="fill-muted-foreground text-xs"
                        >
                          {centerLabelSubtext}
                        </tspan>
                      </text>
                    )
                  }
                  return null
                }}
              />
            )}
          </Pie>
          {showLegend && (
            <ChartLegend
              content={({ payload }) => {
                if (!payload?.length) return null
                
                // Limit to 10 items, show remaining count
                const maxItems = 10
                const visibleItems = payload.slice(0, maxItems)
                const remainingCount = payload.length - maxItems
                
                return (
                  <div
                    className={`flex items-center justify-center ${legendGap} flex-wrap px-2 overflow-hidden`}
                    style={{ maxHeight: legendHeight }}
                  >
                    {visibleItems.map((item, index) => {
                      // Access the name from the data entry
                      const dataEntry = data.find(d => d.fill === item.color)
                      const itemName = dataEntry?.name || item.value || `Item ${index + 1}`
                      const itemConfig = chartConfig[itemName] || { label: itemName }
                      
                      return (
                        <div
                          key={itemName}
                          className="flex items-center gap-1.5"
                        >
                          <div
                            className="h-2 w-2 shrink-0 rounded-[2px]"
                            style={{ backgroundColor: item.color }}
                          />
                          <span
                            className={`${legendTextSize} text-muted-foreground leading-tight max-w-[110px] truncate`}
                            title={String(itemConfig.label || itemName)}
                          >
                            {itemConfig.label || itemName}
                          </span>
                        </div>
                      )
                    })}
                    {remainingCount > 0 && (
                      <div className="flex items-center gap-1.5">
                        <div className="h-2 w-2 shrink-0 rounded-[2px] bg-muted-foreground/30" />
                        <span className={`${legendTextSize} text-muted-foreground/70 leading-tight`}>
                          +{remainingCount} more
                        </span>
                      </div>
                    )}
                  </div>
                )
              }}
              verticalAlign={"bottom" as LegendProps["verticalAlign"]}
              wrapperStyle={{ paddingTop: 8, height: legendHeight }}
            />
          )}
        </PieChart>
      </ChartContainer>
    </motion.div>
  )
}

export default DonutChart 